/**
 * UserFeedbackService wraps POST requests to the transaction-services subdomain from the CompletionSurvey component
 * and handles the identification of the user's browser and OS
 *
 * The appropriate URL for the current environment is automatically derived from EnvironmentService
 * when constructing a new instance of UserFeedbackService
 */
import { api } from 'utils';

import { EnvironmentService } from '../environmentService';

export interface UserFeedbackRequest {
  /**
   * boolean value representing user's feedback selection - true = good feedback, false = bad feedback
   */
  thumbsUp: boolean;

  /**
   * optional string containing user's written feedback if any was given
   */
  message: string;

  /**
   * unique ID identifying the user's transaction
   */
  transactionID: string;

  /**
   * identifier for grouping feedback for transactions. Each transaction name should be distinct and easily identifiable (example: sick-pay-apply)
   */
  transaction: string;
}

export interface ApiRequest {
  /**
   * unique ID identifying the user's transaction
   */
  id: string;

  /**
   * identifier for grouping feedback for transactions. Each transaction name should be distinct and easily identifiable (example: sick-pay-apply)
   */
  transaction: string;

  /**
   * boolean value representing user's feedback selection - true = good feedback, false = bad feedback
   */
  thumbsUp: boolean;

  /**
   * optional string containing user's written feedback if any was given
   */
  message?: string;

  /**
   * user's browser name derived from window object
   */
  browser: string;

  /**
   * user's operating system name derived from window object
   */
  operatingSystem: string;
}

export default class UserFeedbackService {
  private adapter: api.Adapter;
  static urlEndpoint = '/v1/feedback';

  /**
   * Constructor will build the baseURL using the current environment or provided override and configure the API apdator to post feedback
   * @param env optional override to target a different user feedback subdomain instead of using the current environment
   */
  constructor(env?: string) {
    this.adapter = new api.Adapter({
      baseURL: this.getUrlForEnvironment(env ?? EnvironmentService.currentEnvironment ?? 'prod'),
      responseType: 'json',
      headers: {
        timeout: 60 * 10 * 1000,
        'content-type': 'application/json',
        accept: 'application/json'
      }
    });
  }

  /**
   * Builds the payload for the user feedback service API in the transaction-services subdomain and posts the request
   * @param request object containing parameters provided by the CompletionSurvey UI component to build the UserFeedback api request
   */
  async sendFeedback(request: UserFeedbackRequest): Promise<any> {
    const browser = this.getUserBrowser();
    const os = this.getUserOS();

    const payload: ApiRequest = {
      id: request.transactionID,
      transaction: request.transaction,
      thumbsUp: request.thumbsUp,
      message: request.message,
      browser: browser,
      operatingSystem: os
    };

    await this.adapter.postValue<ApiRequest>(UserFeedbackService.urlEndpoint, payload);
  }

  /**
   * Selects the appropriate subdomain based on user environment and builds the user feedback request URL
   * (env = uat example: https://uat.feedback.transaction-services-nonprod.service.vic.gov.au)
   *
   * @param env the active environment to construct the transaction-services URL from
   * @returns the fully built URL target for the user feedback API request
   */
  private getUrlForEnvironment(env: string) {
    const isProd = env.indexOf('prod') !== -1;
    const isStage = env.indexOf('stage') !== -1;
    const subDomain = isProd || isStage ? 'transaction-services' : 'transaction-services-nonprod';
    const envPrefix = `${isProd ? 'api' : env}`;
    return `https://${envPrefix}.feedback.${subDomain}.service.vic.gov.au`;
  }

  /**
   * Retrieve the user's browser name from window object
   *
   * @returns a normalised string representation of the user's browser based on userAgent content
   */
  private getUserBrowser() {
    const userAgent = window.navigator.userAgent;

    if (userAgent.indexOf('Edg') > -1) return 'Edge';
    else if (userAgent.indexOf('SamsungBrowser') > -1) return 'SamsungBrowser';
    else if (userAgent.indexOf('Chrome') > -1) return 'Chrome';
    else if (userAgent.indexOf('CriOS') > -1) return 'Chrome';
    else if (userAgent.indexOf('Firefox') > -1) return 'Firefox';
    else if (userAgent.indexOf('FxiOS') > -1) return 'Firefox';
    else if (userAgent.indexOf('Safari') > -1) return 'Safari';
    else if (userAgent.indexOf('MSIE') > -1) return 'Internet Explorer';
    else if (userAgent.indexOf('OPR') > -1 || userAgent.indexOf('Opera')) return 'Opera';
    else return 'Unknown';
  }

  /**
   * Retrieve user's operating system name from the window object
   *
   * @returns a normalised string representation of the user's operating system based on userAgent content
   */
  private getUserOS() {
    const userAgent = window.navigator.userAgent;

    if (userAgent.indexOf('like Mac') > -1) return 'iOS';
    else if (userAgent.indexOf('Mac') > -1) return 'Mac';
    else if (userAgent.indexOf('Win') > -1) return 'Windows';
    else if (userAgent.indexOf('Android') > -1) return 'Android';
    else if (userAgent.indexOf('Linux') > -1) return 'Linux';
    else return 'Unknown';
  }
}
